/**
 * Helpers for the MCP "Connect an AI client" consent flow (issue #245).
 *
 * When a user reaches the consent screen without an active SpotHR session, the
 * pending OAuth request is stashed here before login is triggered, then
 * restored after login completes (see `applyOrgToken` in the callback screen)
 * so the OAuth query parameters survive the round trip.
 */

const STORAGE_KEY = 'spothr.mcpPendingConsent';

interface PendingMcpConsent {
  // Absent for the generic (marketplace) flow, where the workspace is not known
  // until the user picks one after signing in.
  orgId?: string | null;
  search: string;
}

export const buildMcpConsentPath = (orgId: string, search: string): string => {
  const normalizedSearch = search.startsWith('?') ? search : `?${search}`;
  return `/${orgId}/mcp-consent${normalizedSearch}`;
};

export const savePendingMcpConsent = (orgId: string, search: string): void => {
  try {
    const value: PendingMcpConsent = { orgId, search };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage may be unavailable (private mode); the signed-in path still works.
  }
};

/**
 * Stash a pending consent for the generic (marketplace) flow, where no org is
 * chosen yet. The workspace is resolved later, when the user picks one after
 * platform sign-in, and the consent then resumes on that org's consent screen.
 */
export const savePendingGenericMcpConsent = (search: string): void => {
  try {
    const value: PendingMcpConsent = { search };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage may be unavailable (private mode).
  }
};

/**
 * Whether an MCP consent is mid-flight (without consuming it). Used to keep the
 * post-login workspace picker visible even for a single-workspace user, so they
 * explicitly choose which workspace to connect rather than being auto-selected.
 */
export const hasPendingMcpConsent = (): boolean => {
  try {
    return !!window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
};

/**
 * Return (and clear) the pending consent path for the org the user just signed
 * in to, or null when there is nothing pending. A per-org pending only resumes
 * for its own org; a generic pending resumes for whichever org was picked.
 */
export const consumePendingMcpConsent = (orgId: string): string | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const pending = JSON.parse(raw) as PendingMcpConsent;
    if (!pending) {
      return null;
    }
    // Both per-org and generic pendings resume on the chosen workspace's own
    // consent screen — the proven flow the per-org connector already uses. A
    // per-org pending only resumes for its own org; a generic pending (no org
    // chosen yet) resumes for whichever workspace was just picked.
    if (pending.orgId && pending.orgId !== orgId) {
      return null;
    }
    window.localStorage.removeItem(STORAGE_KEY);
    return buildMcpConsentPath(orgId, pending.search);
  } catch {
    return null;
  }
};
